import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { findMatches } from "@/lib/pdfSearch";
import { usePdfDocument, itemRangeRect } from "./usePdfDocument";
import { PdfPage } from "./PdfPage";
import { ViewerSearchBar } from "./ViewerSearchBar";

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function PdfView({ blob, zoom, onPageInfo }) {
  const { pdf, pages, indexes, indexing, error } = usePdfDocument(blob);
  const rootRef = useRef(null);
  const pageEls = useRef({});
  const [width, setWidth] = useState(0);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const dq = useDebounced(query, 250);

  useEffect(() => {
    const el = rootRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const maxW = pages.reduce((m, p) => Math.max(m, p.w), 0);
  const scale = maxW && width ? Math.min((width - 16) / maxW, 1.5) * zoom : 1;

  const matches = useMemo(() => findMatches(indexes, dq), [indexes, dq]);
  const highlightsByPage = useMemo(() => {
    const by = {};
    matches.forEach((m, mi) => m.ranges.forEach((r) => {
      (by[m.page] ||= []).push({ ...itemRangeRect(pages[m.page], r), m: mi });
    }));
    return by;
  }, [matches, pages]);
  const noText = !indexing && pdf && indexes.length > 0 && indexes.every((i) => !i.text.length);

  useEffect(() => { setActive(0); }, [dq]);

  useEffect(() => {
    const m = matches[active]; const root = rootRef.current;
    if (!m || !root) return;
    const el = pageEls.current[m.page + 1]; if (!el) return;
    const r = highlightsByPage[m.page].find((h) => h.m === active);
    root.scrollTo({ top: el.offsetTop + r.y * scale - root.clientHeight / 3, left: Math.max(0, el.offsetLeft + r.x * scale - root.clientWidth / 3), behavior: "smooth" });
  }, [active, matches, highlightsByPage, scale]);

  const onScroll = () => {
    const root = rootRef.current; if (!root || !pages.length) return;
    const y = root.scrollTop + root.clientHeight / 3;
    let cur = 1;
    for (let i = 1; i <= pages.length; i++) if (pageEls.current[i]?.offsetTop <= y) cur = i;
    onPageInfo?.(cur, pages.length);
  };
  useEffect(() => { if (pages.length) onPageInfo?.(1, pages.length); }, [pages.length, onPageInfo]);

  const pageRef = useCallback((n, el) => { pageEls.current[n] = el; }, []);
  const step = (d) => matches.length && setActive((a) => (a + d + matches.length) % matches.length);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ViewerSearchBar query={query} setQuery={setQuery} count={matches.length} active={active}
        onPrev={() => step(-1)} onNext={() => step(1)} indexing={indexing} noText={noText} />
      <div ref={rootRef} onScroll={onScroll} className="flex-1 overflow-auto bg-slate-800/60 overscroll-contain" data-testid="pdf-scroll-container">
        {error ? <div className="text-red-400 text-sm p-6 text-center" data-testid="pdf-error">{error}</div>
          : !pdf ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          : (
            <div className="relative flex flex-col gap-3 p-2 w-max min-w-full items-center">
              {pages.map((p, i) => (
                <PdfPage key={i} pdf={pdf} num={i + 1} size={p} scale={scale} highlights={highlightsByPage[i] || []}
                  activeMatch={matches[active]?.page === i ? active : -1} rootRef={rootRef} pageRef={pageRef} />
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
